import * as turf from '@turf/turf'

const RTL_COMMAND = 20
const LAND_COMMAND = 21
const HORIZONTAL_SPEED_METERS_PER_SECOND = 12
const VERTICAL_SPEED_METERS_PER_SECOND = 5

function parseRouteTasks (routeData) {
  try {
    const parsedRoute = typeof routeData === 'string' ? JSON.parse(routeData) : routeData
    const tasks = parsedRoute && parsedRoute.questAss && parsedRoute.questAss.tasks
    return Array.isArray(tasks) ? tasks : null
  } catch (error) {
    return null
  }
}

function routePoint (task) {
  const lon = Number(task && task.y)
  const lat = Number(task && task.x)
  const alt = Number(task && task.z)
  return Number.isFinite(lon) && Number.isFinite(lat) && Number.isFinite(alt)
    ? { lon, lat, alt }
    : null
}

function segmentDistanceMeters (start, end) {
  if (!start || !end) return 0
  const horizontal = turf.distance(
    turf.point([start.lon, start.lat]),
    turf.point([end.lon, end.lat]),
    { units: 'kilometers' }
  ) * 1000
  return Math.hypot(horizontal, end.alt - start.alt)
}

/**
 * 根据飞控当前目标航点和飞机实时位置计算整条任务航线的连续进度。
 * MISSION_CURRENT.seq 表示“正在飞往”的任务项，而不是最后完成的任务项。
 */
export function calculateMissionProgress (routeData, currentSeq, livePosition) {
  const tasks = parseRouteTasks(routeData)
  const seq = Number(currentSeq)
  if (!tasks || tasks.length < 2 || !Number.isInteger(seq)) return null

  // 第 0 项是 Home 参考点，不属于飞行任务进度；RTL 的实际终点是起飞点。
  const missionTasks = tasks.slice(1)
  const nodes = missionTasks.map((task, index) => ({
    seq: Number.isInteger(Number(task.seq)) ? Number(task.seq) : index + 1,
    point: routePoint(task)
  }))
  if (nodes.some(node => !node.point)) return null
  if (Number(missionTasks[missionTasks.length - 1].command) === RTL_COMMAND && nodes.length > 1) {
    nodes[nodes.length - 1].point = { ...nodes[0].point, alt: 0 }
  }

  const segmentLengths = []
  let totalDistance = 0
  for (let index = 1; index < nodes.length; index += 1) {
    const length = segmentDistanceMeters(nodes[index - 1].point, nodes[index].point)
    segmentLengths.push(length)
    totalDistance += length
  }
  if (totalDistance <= 0) return null

  if (seq === Number(tasks[0].seq || 0)) return 0
  const targetIndex = nodes.findIndex(node => node.seq === seq)
  if (targetIndex < 0) return null
  if (targetIndex === 0) return 0

  let completedDistance = segmentLengths.slice(0, targetIndex - 1).reduce((sum, length) => sum + length, 0)
  const segmentLength = segmentLengths[targetIndex - 1]
  const position = Array.isArray(livePosition) && livePosition.length >= 3
    ? routePoint({ y: livePosition[0], x: livePosition[1], z: livePosition[2] })
    : null
  if (position && segmentLength > 0) {
    const remainingDistance = segmentDistanceMeters(position, nodes[targetIndex].point)
    const segmentProgress = Math.max(0, Math.min(1, 1 - remainingDistance / segmentLength))
    completedDistance += segmentLength * segmentProgress
  }

  return Math.max(0, Math.min(100, completedDistance / totalDistance * 100))
}

export function getMissionDeliveryProgress (routeData) {
  const tasks = parseRouteTasks(routeData)
  if (!tasks || tasks.length < 2) return null
  const missionTasks = tasks.slice(1)
  const points = missionTasks.map(routePoint)
  if (points.some(point => !point)) return null
  if (Number(missionTasks[missionTasks.length - 1].command) === RTL_COMMAND && points.length > 1) {
    points[points.length - 1] = { ...points[0], alt: 0 }
  }

  let totalDistance = 0
  let deliveryDistance = Number(missionTasks[0].command) === 94 ? 0 : null
  for (let index = 1; index < missionTasks.length; index += 1) {
    totalDistance += segmentDistanceMeters(points[index - 1], points[index])
    if (deliveryDistance === null && Number(missionTasks[index].command) === 94) deliveryDistance = totalDistance
  }
  if (deliveryDistance === null || totalDistance <= 0) return null
  return Math.max(0, Math.min(100, deliveryDistance / totalDistance * 100))
}

export function calculateRouteMetrics (routeData) {
  const tasks = parseRouteTasks(routeData)
  if (!tasks || tasks.length < 2) return null

  const finalCommand = Number(tasks[tasks.length - 1].command)
  const hasFinishTask = [RTL_COMMAND, LAND_COMMAND].includes(finalCommand)
  // Home 仅为参考点，RTL/LAND 仅为结束指令，两者都不是普通航点。
  const flightPoints = tasks.slice(1, hasFinishTask ? -1 : undefined)
  if (!flightPoints.length || flightPoints.some(point => (
    !Number.isFinite(Number(point.x)) ||
    !Number.isFinite(Number(point.y)) ||
    !Number.isFinite(Number(point.z))
  ))) return null

  // RTL 指令中的坐标可能为空或为历史占位值，实际飞行规则是返回起飞点。
  const routePoints = [...flightPoints]
  if (finalCommand === RTL_COMMAND && flightPoints.length > 1) {
    routePoints.push(flightPoints[0])
  }

  // 起飞与降落按垂直航段计算；航点之间使用水平距离与高度差组成的三维斜距。
  const takeoffVerticalMeters = Math.abs(Number(routePoints[0].z))
  let distanceMeters = takeoffVerticalMeters
  let durationSeconds = takeoffVerticalMeters / VERTICAL_SPEED_METERS_PER_SECOND
  for (let index = 1; index < routePoints.length; index++) {
    const previous = routePoints[index - 1]
    const current = routePoints[index]
    const horizontalDistanceMeters = turf.distance(
      turf.point([Number(previous.y), Number(previous.x)]),
      turf.point([Number(current.y), Number(current.x)]),
      { units: 'kilometers' }
    ) * 1000
    const verticalDistanceMeters = Math.abs(Number(current.z) - Number(previous.z))
    distanceMeters += Math.hypot(horizontalDistanceMeters, verticalDistanceMeters)
    // 水平飞行与高度变化可同时进行，航段耗时由较慢方向决定。
    durationSeconds += Math.max(
      horizontalDistanceMeters / HORIZONTAL_SPEED_METERS_PER_SECOND,
      verticalDistanceMeters / VERTICAL_SPEED_METERS_PER_SECOND
    )
  }
  const landingVerticalMeters = Math.abs(Number(routePoints[routePoints.length - 1].z))
  distanceMeters += landingVerticalMeters
  durationSeconds += landingVerticalMeters / VERTICAL_SPEED_METERS_PER_SECOND

  return {
    pointCount: flightPoints.length,
    distanceMeters,
    durationSeconds
  }
}

export function formatRouteDistance (distanceMeters) {
  if (!Number.isFinite(distanceMeters) || distanceMeters <= 0) return '0 m'
  if (distanceMeters < 1000) return `${Math.round(distanceMeters)} m`
  return `${(distanceMeters / 1000).toFixed(2)} km`
}

export function formatFlightDuration (durationSeconds) {
  if (!Number.isFinite(durationSeconds) || durationSeconds <= 0) return '0 秒'
  const totalSeconds = Math.ceil(durationSeconds)
  const hours = Math.floor(totalSeconds / 3600)
  const minutes = Math.floor((totalSeconds % 3600) / 60)
  const seconds = totalSeconds % 60
  if (hours) return `${hours} 小时 ${minutes} 分`
  if (minutes) return `${minutes} 分 ${seconds} 秒`
  return `${seconds} 秒`
}
